// ============================================
// SERVICIO — CONSTANTES AGRONÓMICAS
// ============================================
// Eficiencias por tipo de riego, texturas de suelo y valores por defecto
// (factor p, Zr, AD, margen de alerta). Fuente única: el backend
// (`agrobyte_api/helpers/agronomia.js`, GET /api/agronomia/constantes).
//
// Antes cada uno de estos números vivía suelto en el frontend, y algunos ya
// diferían entre pantallas: el factor `p` estaba en 0.5 en useDemandaRiego y en
// 0.65 en CalendarioRiegos; el margen de la alerta de AD, en 10 en "Configurar
// cuartel" y en 5 en "Alertas".
//
// Los FALLBACKS de este archivo son los mismos valores del backend y existen
// solo para que la app siga funcionando si la petición falla. No son una
// segunda fuente: si cambian allá, cambian acá.

import { ref, computed } from 'vue';
import { api } from './api';

export const CONSTANTES_FALLBACK = {
  tiposRiego: [
    { value: 'goteo', label: 'Goteo', ef: 90 },
    { value: 'aspersion', label: 'Aspersión', ef: 75 },
    { value: 'taza', label: 'Taza', ef: 65 },
    { value: 'surco', label: 'Surco', ef: 45 },
    { value: 'tendido', label: 'Tendido', ef: 30 }
  ],
  texturas: [
    { value: 70, label: 'Arenoso' },
    { value: 100, label: 'Franco-arenoso' },
    { value: 140, label: 'Franco' },
    { value: 160, label: 'Franco-arcilloso' },
    { value: 180, label: 'Arcilloso' }
  ],
  defaults: {
    p: 0.65,
    eficiencia: 90,
    zr: 1.2,
    adMmPorMetro: 140,
    margenAd: 10,
    umbralPresion: 0.5
  },
  precipEfectiva: {
    metodos: [
      { value: 'usda', label: 'USDA-SCS' },
      { value: 'pct', label: 'Porcentaje fijo' }
    ],
    metodoDefault: 'usda',
    porcentajeDefault: 80
  }
};

// Estado REACTIVO y no una constante de módulo: la carga es asíncrona, así que
// un `export const TIPOS_RIEGO = ...` evaluado al importar se congelaría con el
// fallback y la respuesta del backend no llegaría nunca a la pantalla — que es
// justo lo contrario de centralizar.
//
// Se guarda además la PROMESA para que varias pantallas montándose a la vez
// compartan una sola petición en vez de disparar una cada una.
const _constantes = ref(CONSTANTES_FALLBACK);
let _promesa = null;

/** Constantes ya cargadas (o los fallbacks). Reactivo. */
export function constantesAgronomicas() {
  return _constantes.value;
}

export const tiposRiego = computed(() => _constantes.value.tiposRiego);
export const texturas = computed(() => _constantes.value.texturas);
export const defaultsAgronomicos = computed(() => _constantes.value.defaults);
export const precipEfectivaConfig = computed(() => _constantes.value.precipEfectiva);

/** Carga las constantes del backend. Nunca lanza: ante un fallo deja los fallbacks. */
export function cargarConstantesAgronomicas() {
  if (_promesa) return _promesa;
  _promesa = api.get('/api/agronomia/constantes')
    .then((res) => {
      const data = res?.data ?? res;
      if (data && Array.isArray(data.tiposRiego) && data.defaults) {
        _constantes.value = { ...CONSTANTES_FALLBACK, ...data };
      }
      return _constantes.value;
    })
    .catch(() => _constantes.value);
  return _promesa;
}

/** Eficiencia (%) de un tipo de riego, o el default si no se reconoce. */
export function eficienciaDeTipoRiego(tipo) {
  const c = constantesAgronomicas();
  const t = c.tiposRiego.find((x) => x.value === String(tipo ?? '').trim().toLowerCase());
  return t ? t.ef : c.defaults.eficiencia;
}

export default { constantesAgronomicas, cargarConstantesAgronomicas, eficienciaDeTipoRiego };
